/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import UserSegmentToggle from '../common/UserSegmentToggle';
import { useUserSegment, watchFilterValue } from '../../context/UserSegmentContext';
import { formatUTCDate } from '../../utils/utils';
import ParticipationRate from '../ParticipationRate';
import ExportButton from '../active-users/ExportButton';
import DateRangeBar, { fmt, presetRange } from '../active-users/DateRangeBar';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import { readSavedFilters, usePersistFilters } from '../../hooks/filterPersistence';
import '../active-users/active-users.css';

const LIMIT = 20;
const STATE_KEY = 'medicineListState';
const EXPORT_CAP = 50000;
const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'country', label: 'Country' },
  { key: 'mobile_app_version', label: 'App Version' },
  { key: 'subscription_status', label: 'Subscription' },
  { key: 'medicine_log_count', label: 'Medicine Log Count' },
  { key: 'medicine_master_count', label: 'Medicine Master Count' },
  { key: 'latest_medicine_log_time', label: 'Last Medicine Log' },
  { key: 'latest_medicine_master_time', label: 'Last Medicine Master' },
];

const SORTABLE = {
  medicine_log_count: 'Medicine Log Count',
  medicine_master_count: 'Medicine Master Count',
  latest_medicine_log_time: 'Last Medicine Log',
  latest_medicine_master_time: 'Last Medicine Master',
};

const UserMedicineCount = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();                 // global watch/without/all (feature 023)
  const userSegment = watchFilterValue(segment);        // 'with_watch' | 'without_watch' | ''

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [users, setUsers] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [totalLog, setTotalLog] = useState(0);
  const [totalMaster, setTotalMaster] = useState(0);
  const [page, setPage] = useState(1);
  const [truncated, setTruncated] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFrom, setDisplayFrom] = useState(null);
  const [displayTo, setDisplayTo] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Restore the list view on return from a drill-down (Back); else default Last 30.
    const saved = readSavedFilters(STATE_KEY);
    if (saved) {
      setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
      setDisplayFrom(saved.fromDate ? new Date(saved.fromDate) : null);
      setDisplayTo(saved.toDate ? new Date(saved.toDate) : null);
      setPreset(saved.preset || null);
      setSearchValue(saved.searchValue || '');
      setSortField(saved.sortField || ''); setSortOrder(saved.sortOrder || '');
      setPage(saved.page || 1);
    } else {
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFrom(new Date(fromStr)); setDisplayTo(new Date(toStr));
      setPreset('last30');
    }
    refreshToken();
  }, []);

  useEffect(() => {
    if (token) loadData(page, fromDate, toDate, searchValue, sortField, sortOrder);
  }, [token]);

  const didMountSeg = useRef(false);
  useEffect(() => {
    if (!didMountSeg.current) { didMountSeg.current = true; return; }
    if (!token) return;
    setPage(1);
    loadData(1, fromDate, toDate, searchValue, sortField, sortOrder);
  }, [segment]);

  // Snapshot filters so the exact view is restored on Back (remount).
  usePersistFilters(STATE_KEY, { fromDate, toDate, preset, searchValue, sortField, sortOrder, page });

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const seg = () => (userSegment ? `&user_segment=${userSegment}` : '');

  const loadData = async (p, f, t, search, sf, so) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(
        `${baseUrl}/admin/api/user-medicine?page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${search}&sortField=${sf}&sortOrder=${so}${seg()}`,
        auth());
      const data = res.data.data || [];
      setUsers(data);
      setTotalItem(res.data.totalItem || 0);
      setTotalUsers(res.data.userCount?.[0]?.user_count || 0);
      setTotalUserBase(res.data.totalUserCount || 0);
      setTotalLog(data?.[0]?.total_medicine_log_count || 0);
      setTotalMaster(data?.[0]?.total_medicine_master_count || 0);
    } catch (err) {
      if (err?.response?.status === 401) setError('auth'); else setError('load');
    } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => { setPage(pageNumber); loadData(pageNumber, fromDate, toDate, searchValue, sortField, sortOrder); };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFrom(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayTo(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFrom(fromStr ? new Date(fromStr) : null); setDisplayTo(toStr ? new Date(toStr) : null);
    setPage(1); loadData(1, fromStr, toStr, searchValue, sortField, sortOrder);
  };
  const handleSearch = () => { setPage(1); loadData(1, fromDate, toDate, searchValue, sortField, sortOrder); };
  const clearFilter = () => {
    const { fromStr, toStr } = presetRange('last30', new Date());
    setFromDate(fromStr); setToDate(toStr); setDisplayFrom(new Date(fromStr)); setDisplayTo(new Date(toStr));
    setPreset('last30'); setSearchValue(''); setSortField(''); setSortOrder(''); setPage(1);
    loadData(1, fromStr, toStr, '', '', '');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadData(1, fromDate, toDate, searchValue, field, order);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/user-medicine-export?from_date=${fromDate}&to_date=${toDate}&search=${searchValue}${seg()}`, auth());
    const data = res.data.data || [];
    setTruncated(data.length >= EXPORT_CAP);
    return data;
  };

  const hasFilters = preset !== 'last30' || !!searchValue || !!sortField;
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  const arrow = (field) => (sortField === field ? (sortOrder === 'ASC' ? ' 🔼' : ' 🔽') : '');

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb"><b>Medicine</b></span>
            <span className="au-route">/user-medicine</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Medicine</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the medicine overview. Please try again." onRetry={() => loadData(page, fromDate, toDate, searchValue, sortField, sortOrder)} />
          ) : (
            <>
              <div className="au-cards" data-testid="user-medicine-summary">
                <div className="au-card">
                  <span className="au-card__label">Total Medicine Log</span>
                  <span className="au-card__value tnum">{Number(totalLog).toLocaleString()}</span>
                </div>
                <div className="au-card">
                  <span className="au-card__label">Total Medicine Master</span>
                  <span className="au-card__value tnum">{Number(totalMaster).toLocaleString()}</span>
                </div>
                <div className="au-card">
                  <span className="au-card__label">Total Users</span>
                  <span className="au-card__value tnum">{Number(totalUsers).toLocaleString()}</span>
                  <span className="au-card__sub"><ParticipationRate count={totalUsers} total={totalUserBase} /></span>
                </div>
              </div>

              <DateRangeBar
                leading={(
                  <input
                    className="au-input"
                    type="text"
                    placeholder="Search by name or email"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    aria-label="Search users by name or email"
                  />
                )}
                preset={preset}
                displayFrom={displayFrom}
                displayTo={displayTo}
                hasRange={hasFilters}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="medicine-range-note">
                The date range filters users by their medicine <b>record date (UTC)</b> — <code>DATE(created_time_stamp)</code>.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all records (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : users.length === 0 ? (
                <EmptyState title="No users in the selected range/segment" hasFilters onClear={clearFilter}
                  message="No users have medicine records matching the current range and segment." />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Users with medicine activity</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="user-medicine.csv" />
                  </div>
                  {truncated && (
                    <p className="au-range-note" data-testid="medicine-export-truncated">
                      Export limited to the most recent {EXPORT_CAP.toLocaleString()} users.
                    </p>
                  )}
                  <div className="au-table-wrap">
                    <table className="au-table" data-testid="user-medicine-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Country</th>
                          <th>App Version</th><th>Subscription</th>
                          {Object.entries(SORTABLE).map(([field, label]) => (
                            <th key={field} className="au-sortable" style={{ cursor: 'pointer' }}
                              onClick={() => handleSort(field)} aria-label={`Sort by ${label}`}>
                              {label}{arrow(field)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={user.id}>
                            <td data-label="No" className="tnum">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{user.name}</td>
                            <td data-label="Email" className="au-ellipsis">{user.email}</td>
                            <td data-label="Country">{user.country}</td>
                            <td data-label="App Version">{user.mobile_app_version}</td>
                            <td data-label="Subscription">{user.subscription_status}</td>
                            <td data-label="Medicine Log Count" className="tnum">
                              <Link to={`/medicine-log-details/${btoa(user.id)}`}>{user.medicine_log_count}</Link>
                            </td>
                            <td data-label="Medicine Master Count" className="tnum">
                              <Link to={`/medicine-master-details/${btoa(user.id)}`}>{user.medicine_master_count}</Link>
                            </td>
                            <td data-label="Last Medicine Log">{formatUTCDate(user.latest_medicine_log_time)}</td>
                            <td data-label="Last Medicine Master">{formatUTCDate(user.latest_medicine_master_time)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserMedicineCount;
